import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge, type Tone } from '@/components/ui/Badge'
import { ButtonLink } from '@/components/ui/Button'
import { Page, PageHeader } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { formatAgo, formatDateShort } from '@/lib/dates'
import { formatCouple, pluralise, townFrom } from '@/lib/format'
import { describeError } from '@/lib/supabase'
import { inquiryUrgency, useInquiries, type InquiryStatus, type InquiryWithRelations } from './queries'

/**
 * The enquiry list.
 *
 * Ordered newest first and coloured by how long an unanswered one has been
 * sitting there, because the only thing that matters on this screen is whether
 * somebody is waiting for a reply. Answered enquiries fade back to neutral.
 */

export const STATUS_LABEL: Record<InquiryStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  quoted: 'Quoted',
  booked: 'Booked',
  lost: 'Lost',
  spam: 'Spam',
}

export const STATUS_TONE: Record<InquiryStatus, Tone> = {
  new: 'amber',
  reviewing: 'ink',
  quoted: 'ink',
  booked: 'sage',
  lost: 'neutral',
  spam: 'neutral',
}

type Filter = 'open' | 'all' | 'archived'

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'open', label: 'Needs attention' },
  { value: 'all', label: 'All' },
  { value: 'archived', label: 'Archived' },
]

export function EnquiriesPage() {
  const [filter, setFilter] = useState<Filter>('open')
  const query = useInquiries(filter)

  const inquiries = query.data ?? []
  const waiting = inquiries.filter((inquiry) => inquiry.status === 'new').length

  return (
    <Page>
      <PageHeader
        eyebrow="Projects"
        title="Enquiries"
        meta={
          query.data ? (
            <>
              <span>{pluralise(inquiries.length, 'enquiry', 'enquiries')}</span>
              {waiting > 0 ? <span className="text-amber">{waiting} awaiting a reply</span> : null}
            </>
          ) : null
        }
        actions={<ButtonLink to="/enquiries/new" variant="primary">Log an enquiry</ButtonLink>}
      />

      <nav aria-label="Filter enquiries" className="flex gap-0 border-b border-b-[var(--border-hairline)]">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setFilter(option.value)}
            aria-current={filter === option.value ? 'true' : undefined}
            className={cn(
              'label -mb-px border-b-2 px-3 py-2.5',
              filter === option.value ? 'border-b-accent font-semibold text-accent' : 'border-b-transparent hover:text-ink',
            )}
          >
            {option.label}
          </button>
        ))}
      </nav>

      {query.isPending ? (
        <Loading label="Loading enquiries" />
      ) : query.isError ? (
        <div className="pt-6">
          <ErrorState
            title="Enquiries could not load"
            detail={describeError(query.error)}
            onRetry={() => void query.refetch()}
          />
        </div>
      ) : inquiries.length === 0 ? (
        <EmptyState
          className="mt-6"
          title={filter === 'open' ? 'Nothing waiting on you' : 'No enquiries here'}
          body={
            filter === 'open'
              ? 'Every enquiry has been answered or moved along. New ones from the website will appear here.'
              : 'Enquiries from the website form arrive here. You can also log one by hand after a phone call.'
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="ledger mt-1 min-w-[46rem]">
            <thead>
              <tr>
                <th className="label">Couple</th>
                <th className="label">Wedding date</th>
                <th className="label">Venue</th>
                <th className="label">Wants</th>
                <th className="label">Received</th>
                <th className="label">Status</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inquiry) => (
                <EnquiryRow key={inquiry.id} inquiry={inquiry} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  )
}

const SERVICES_LABEL = {
  photography: 'Photo',
  videography: 'Film',
  both: 'Photo + film',
} as const

function EnquiryRow({ inquiry }: { inquiry: InquiryWithRelations }) {
  const urgency = inquiryUrgency(inquiry)

  return (
    <tr>
      <td>
        <Link
          to={`/enquiries/${inquiry.id}`}
          className="display block truncate text-[0.9375rem] underline decoration-transparent underline-offset-4 hover:decoration-[var(--color-rule-strong)]"
        >
          {formatCouple(
            { first_name: inquiry.first_name, last_name: inquiry.last_name },
            inquiry.partner_first_name ? { first_name: inquiry.partner_first_name, last_name: inquiry.partner_last_name } : null,
          )}
        </Link>
        <span className="text-[length:var(--text-2xs)] text-taupe">{inquiry.email}</span>
      </td>

      <td className="whitespace-nowrap">
        {inquiry.event_date ? (
          <time dateTime={inquiry.event_date}>{formatDateShort(inquiry.event_date)}</time>
        ) : (
          <span className="text-taupe">Not given</span>
        )}
      </td>

      <td className="max-w-[12rem] truncate">
        {inquiry.venue?.name ?? (inquiry.venue_text ? townFrom(inquiry.venue_text) : <span className="text-taupe">—</span>)}
      </td>

      <td className="whitespace-nowrap text-taupe">{inquiry.services ? SERVICES_LABEL[inquiry.services] : '—'}</td>

      <td
        className={cn(
          'whitespace-nowrap',
          urgency === 'late' ? 'text-clay' : urgency === 'waiting' ? 'text-amber' : 'text-taupe',
        )}
      >
        {formatAgo(inquiry.received_at)}
      </td>

      <td className="whitespace-nowrap">
        {inquiry.job ? (
          <Link to={`/weddings/${inquiry.job.id}`} className="underline decoration-[var(--color-rule-strong)] underline-offset-4">
            <Badge tone="sage">{`Wedding ${inquiry.job.job_number}`}</Badge>
          </Link>
        ) : (
          <Badge tone={STATUS_TONE[inquiry.status]}>{STATUS_LABEL[inquiry.status]}</Badge>
        )}
      </td>
    </tr>
  )
}
